<!--
  @copyright 汽车商贸管理平台 V1.0
  @year 2026
  @description Login
-->
<template>
  <div class="main">
    <div class="content">
      <div class="content-top suspended-box">
        <van-form ref="form" class="custom-form">
          <!-- 手机号码 -->
          <van-field
            class="custom-field"
            label="手机号码"
            v-model="loginForm.mobile"
            placeholder="请输入手机号码"
            :rules="telRules"
            name="mobile"
          ></van-field>
          <!-- 验证码 -->
          <van-field
            class="custom-field"
            label="短信验证码"
            v-model="loginForm.code"
            placeholder="6位数字验证码"
          >
            <template #button>
              <van-button
                class="custom-button"
                size="small"
                type="primary"
                :disabled="!isPhoneValid || isSending"
                @click="handleSendCode"
                >{{ codeMsg }}</van-button
              >
            </template>
          </van-field>
        </van-form>

        <!-- 记住我 -->
        <div class="remember-row">
          <van-checkbox v-model="rememberMe" shape="square"
            >7天内免登录</van-checkbox
          >
        </div>
      </div>
      <div class="custom-submit-button">
        <van-button
          style="background-color: #007aff; border-radius: 5px; height: 100%"
          type="primary"
          block
          @click="onSubmit"
          native-type="submit"
          >提交</van-button
        >
      </div>
    </div>

    <!-- 简单数学验证码弹窗 -->
    <van-dialog
      v-model="showCaptcha"
      title="安全验证"
      show-cancel-button
      :before-close="beforeDialogClose"
    >
      <div class="captcha-content">
        <div class="captcha-question">
          请计算：<span class="captcha-expr">{{ captchaExpr }}</span> = ？
        </div>
        <van-field
          v-model="captchaAnswer"
          type="number"
          placeholder="请输入计算结果"
          class="captcha-input"
        />
      </div>
    </van-dialog>
  </div>
</template>

<script>
import {
  loginByPhoneAndCode,
  getPhoneCode,
  getDingTalkCorpId,
  getDingTalkClientId,
  dingTalkLogin,
} from "../api/index";
import { isDingTalkEnv, getDingTalkAuthCode } from "../utils/dingtalk";
import { Toast } from "vant";

export default {
  name: "Login",
  data() {
    return {
      loginForm: {
        mobile: "",
        code: "",
      },
      codeNum: 60,
      codeMsg: "获取验证码",
      isSending: false,
      timer: null,
      hasSentBefore: false,
      showCaptcha: false,
      captchaExpr: "",
      captchaResult: 0,
      captchaAnswer: "",
      rememberMe: false,
      isDingAuto: false, // 钉钉免登进行中
      oauthFired: false, // OAuth2 兜底已触发(防止循环跳转)
      telRules: [
        { required: true, message: "请输入手机号码", trigger: "blur" },
        {
          pattern: /^1[345789]\d{9}$/,
          message: "请输入正确的手机号码格式",
          trigger: "blur",
        },
      ],
    };
  },
  computed: {
    isPhoneValid() {
      return /^1[345789]\d{9}$/.test(this.loginForm.mobile);
    },
    // 需要自动尝试钉钉登录：钉钉容器内，或从深链(通知/审批链接)带 redirect 进来的
    allowAutoDing() {
      if (isDingTalkEnv()) return true;
      return !!(this.$route.query && this.$route.query.redirect);
    },
  },
  mounted() {
    // 钉钉容器或深链进入：自动尝试免登(dd → OAuth2 兜底)，失败再回落手机号登录
    if (this.allowAutoDing) {
      this.tryDingTalkLogin();
    }
  },
  methods: {
    // 登录/免登成功后：有 redirect 回原页（钉钉审批通知直达），否则回首页
    goAfterLogin() {
      const redirect = this.$route.query.redirect;
      if (redirect && redirect.indexOf("/") === 0) {
        this.$router.replace(redirect);
      } else {
        this.$router.replace("/");
      }
    },
    // 钉钉免登：jsapi → authCode → 后端换 token；失败转 OAuth2 兜底或回落手机号登录
    async tryDingTalkLogin() {
      if (this.isDingAuto || !this.allowAutoDing) return;
      if (localStorage.getItem("token") || sessionStorage.getItem("token")) return;

      // 非钉钉容器(PC钉钉/浏览器)的深链：跳过 dd 等待，直接走 OAuth2 授权(更快)
      if (!isDingTalkEnv()) {
        await this.tryOAuthFallback();
        return;
      }

      this.isDingAuto = true;
      try {
        console.log("[dd-login] start, inDingTalk=", isDingTalkEnv());
        const corpRes = await getDingTalkCorpId();
        const corpId = corpRes && corpRes.data;
        console.log("[dd-login] corpId=", corpId);
        if (!corpId) {
          Toast({
            message: "钉钉 corpId 未配置，请先在系统设置[DingTalk]填写",
            position: "middle",
            duration: 2500,
          });
          return;
        }

        const authCode = await getDingTalkAuthCode(corpId);
        console.log("[dd-login] authCode len=", authCode ? authCode.length : 0);
        const res = await dingTalkLogin({
          code: authCode,
          browser: "DingTalk",
          os: "DingTalk",
          deviceInfo: navigator.userAgent || "",
        });

        if (res && res.code === 0) {
          Toast.success("钉钉免登成功");
          // 免登会话按 7 天有效存储（与"记住我"一致）
          localStorage.setItem("token", res.data);
          this.$store.commit("LOGIN_IN", res.data);
          this.goAfterLogin();
          return;
        }
        // 后端返回了业务失败（code != 0）：提示后回落手机号登录
        Toast({
          message: (res && res.message) || "钉钉免登失败，请使用手机号登录",
          position: "middle",
          duration: 2500,
        });
      } catch (error) {
        // dd 不可用(非微应用容器/JSAPI 加载失败)：深链或钉钉环境内自动转 OAuth2 授权(无感回跳)
        if (!(error && error.config) && this.allowAutoDing) {
          this.tryOAuthFallback();
          return;
        }
        // axios 类错误已由 http 拦截器统一 Toast；仅对本地 jsapi 异常再提示
        if (!(error && error.config)) {
          Toast({
            message:
              (error && error.message) || "钉钉免登失败，请使用手机号登录",
            position: "middle",
            duration: 2500,
          });
        }
      } finally {
        this.isDingAuto = false;
      }
    },
    // 钉钉 OAuth2 兜底：跳转授权页，回跳 oauth.html → 静默换 token → 回原页
    async tryOAuthFallback() {
      if (this.oauthFired) return;
      this.oauthFired = true;
      try {
        const cRes = await getDingTalkClientId();
        const clientId = cRes && cRes.data;
        if (!clientId) {
          Toast("钉钉免登配置缺失(appKey)，请使用手机号登录");
          return;
        }
        const redirect = (this.$route.query && this.$route.query.redirect) || "/";
        const redirectUri = window.location.origin + "/oauth.html";
        const url =
          "https://login.dingtalk.com/oauth2/auth" +
          "?redirect_uri=" +
          encodeURIComponent(redirectUri) +
          "&response_type=code" +
          "&client_id=" +
          encodeURIComponent(clientId) +
          "&scope=openid" +
          "&state=" +
          encodeURIComponent(redirect) +
          "&prompt=consent";
        console.log("[dd-login] oauth redirect ->", url);
        window.location.href = url;
      } catch (e) {
        console.error("OAuth 跳转失败:", e);
        Toast("进入钉钉授权失败，请使用手机号登录");
      }
    },
    // 获取设备信息
    getDeviceInfo() {
      const ua = navigator.userAgent;
      let browser = "Unknown";
      let os = "Unknown";

      // 浏览器
      if (ua.includes("Chrome")) browser = "Chrome";
      else if (ua.includes("Firefox")) browser = "Firefox";
      else if (ua.includes("Safari")) browser = "Safari";
      else if (ua.includes("Edge")) browser = "Edge";
      else if (ua.includes("WeChat")) browser = "微信";
      else if (ua.includes("Alipay")) browser = "支付宝";

      // 操作系统
      if (ua.includes("Windows")) os = "Windows";
      else if (ua.includes("Mac")) os = "MacOS";
      else if (ua.includes("Linux")) os = "Linux";
      else if (ua.includes("Android")) os = "Android";
      else if (ua.includes("iPhone") || ua.includes("iPad")) os = "iOS";

      return { browser, os, deviceInfo: ua };
    },
    generateCaptcha() {
      const ops = ["+", "-", "×"];
      const op = ops[Math.floor(Math.random() * ops.length)];
      let a, b, result;
      switch (op) {
        case "+":
          a = Math.floor(Math.random() * 50) + 10;
          b = Math.floor(Math.random() * 50) + 10;
          result = a + b;
          break;
        case "-":
          a = Math.floor(Math.random() * 50) + 30;
          b = Math.floor(Math.random() * 30) + 5;
          result = a - b;
          break;
        case "×":
          a = Math.floor(Math.random() * 9) + 2;
          b = Math.floor(Math.random() * 9) + 2;
          result = a * b;
          break;
      }
      this.captchaExpr = `${a} ${op} ${b}`;
      this.captchaResult = result;
      this.captchaAnswer = "";
    },
    handleSendCode() {
      if (this.isSending) return;
      if (!this.loginForm.mobile || !this.isPhoneValid) {
        Toast({
          message: "请输入正确手机号！",
          position: "middle",
          duration: 2000,
        });
        return;
      }
      if (this.hasSentBefore) {
        this.generateCaptcha();
        this.showCaptcha = true;
        return;
      }
      this.sendCode();
    },
    beforeDialogClose(action, done) {
      if (action === "confirm") {
        if (this.captchaAnswer === "") {
          Toast({
            message: "请输入计算结果",
            position: "middle",
            duration: 1500,
          });
          done(false);
          return;
        }
        if (parseInt(this.captchaAnswer) !== this.captchaResult) {
          Toast({
            message: "计算错误，请重试",
            position: "middle",
            duration: 1500,
          });
          this.generateCaptcha();
          done(false);
          return;
        }
        done();
        this.sendCode();
      } else {
        done();
      }
    },
    async sendCode() {
      if (this.isSending) return;
      try {
        const res = await getPhoneCode({ phoneNo: this.loginForm.mobile });
        if (res.code === 0) {
          Toast.success(res.message || "验证码已发送！");
          this.hasSentBefore = true;
          this.startCountdown();
        } else {
          Toast({
            message: res.message || "获取验证码失败",
            position: "middle",
            duration: 2000,
          });
        }
      } catch (error) {
        Toast({
          message: "发送验证码失败",
          position: "middle",
          duration: 2000,
        });
      }
    },
    startCountdown() {
      this.isSending = true;
      let count = this.codeNum;
      this.codeMsg = `重新发送(${count})`;
      this.timer = setInterval(() => {
        count--;
        if (count > 0) {
          this.codeMsg = `重新发送(${count})`;
        } else {
          clearInterval(this.timer);
          this.timer = null;
          this.codeMsg = "获取验证码";
          this.isSending = false;
        }
      }, 1000);
    },
    async onSubmit() {
      if (this.isDingAuto) return; // 钉钉免登进行中，忽略手动提交
      if (!this.loginForm.mobile || !this.isPhoneValid) {
        Toast({
          message: "请输入正确的手机号",
          position: "middle",
          duration: 2000,
        });
        return;
      }
      if (!this.loginForm.code || this.loginForm.code.length !== 6) {
        Toast({
          message: "请输入6位验证码",
          position: "middle",
          duration: 2000,
        });
        return;
      }

      const deviceInfo = this.getDeviceInfo();

      try {
        const data = await loginByPhoneAndCode({
          ...this.loginForm,
          rememberMe: this.rememberMe,
          ...deviceInfo,
        });

        if (data.code === 0) {
          Toast.success("登录成功！");
          //console.log("1. 准备保存 Token，值为:", data.data);
          // 记住我：token存7天，否则关闭浏览器清除
          if (this.rememberMe) {
            localStorage.setItem("token", data.data);
          } else {
            sessionStorage.setItem("token", data.data);
          }
          //console.log("2. Token 保存成功！"); // 看看有没有打印这一行

          this.$store.commit("LOGIN_IN", data.data);
          this.goAfterLogin();
        } else {
          this.$store.commit("LOGIN_OUT");
          Toast({
            message: data.message || "登录失败！",
            position: "middle",
            duration: 2000,
          });
        }
      } catch (error) {
        Toast({ message: "登录请求失败", position: "middle", duration: 2000 });
      }
    },
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  },
};
</script>

<style scoped>
.main {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  height: 100vh;
  padding: 16px;
  box-sizing: border-box;
  background-color: #f5f5f5;
}

.content {
  transform: translateY(22vh);
  width: 100%;
  max-width: 600px;
}

.suspended-box {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  transform: translateY(-16px);
  transition: transform 0.3s ease;
  padding: 16px;
  margin-bottom: 16px;
}

.custom-form {
  display: flex;
  flex-direction: column;
}

.custom-field {
  width: 100%;
  height: 8vh;
  align-content: center;
  align-items: center;
}

.custom-field label {
  font-size: 16px;
  color: #333;
}

.custom-button {
  margin-top: 10px;
  font-size: 14px;
}

.remember-row {
  padding: 8px 16px;
  margin-top: 8px;
}

.custom-submit-button {
  margin-top: 24px;
  height: 8vh;
}

.captcha-content {
  padding: 16px;
}

.captcha-question {
  text-align: center;
  font-size: 18px;
  margin-bottom: 16px;
  color: #333;
}

.captcha-expr {
  font-size: 24px;
  font-weight: bold;
  color: #007aff;
}

@media (max-width: 400px) {
  .content {
    padding: 8px;
  }
  .suspended-box {
    padding: 8px;
  }
  .custom-field label {
    font-size: 14px;
  }
  .custom-button {
    font-size: 12px;
  }
}

@media (min-width: 401px) and (max-width: 768px) {
  .suspended-box {
    padding: 12px;
  }
  .custom-button {
    font-size: 13px;
  }
}

@media (min-width: 769px) {
  .suspended-box {
    padding: 16px;
  }
  .custom-button {
    font-size: 14px;
  }
}
</style>
